import { ArticleAssetTypeEnum, CampaignsGoogleConfigsCampaignTypeEnum } from 'api';
import { getKMSBaseUrl } from 'utils';
import { imgPreview } from '../utils/imgPreview';
import { Crop, PixelCrop } from 'react-image-crop';
import Authentication from 'services/Authentication';
import { canvasPreview } from '../utils/canvasPreview';
import { useEffect, useRef, useState, useMemo } from 'react';
import { useDebounceEffect } from '../../../../../hooks/useDebounceEffect';
import { useGoogleAssetsImagesStore } from 'stores/GoogleAssets/GoogleAssetsImagesStore';
import { useGoogleAssetsBuilderStore } from '../../../../../../../../../../../../../../../stores/GoogleAssets/GoogleAssetsBuilderStore';

const RATIOS = {
  1.91: ArticleAssetTypeEnum.google_marketing_image,
  1: ArticleAssetTypeEnum.google_square_marketing_image,
  [4 / 5]: ArticleAssetTypeEnum.google_portrait_marketing_image
};

export const useImageWithDifferentRatios = (url: string | null) => {
  const { article } = useGoogleAssetsBuilderStore();

  const imgRef = useRef<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [loading, setLoading] = useState(true);
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [completedCrop, setCompletedCrop] = useState<Crop>();
  const [crop, setCrop] = useState<Crop>({ unit: '%', x: 0, y: 0, width: 0, height: 100, aspect: 1 });

  const { images, setImages } = useGoogleAssetsImagesStore();

  const isDisplayCampaign =
    article?.campaign.campaignsGoogleConfig?.campaignType === CampaignsGoogleConfigsCampaignTypeEnum.display;

  const handleCrop = (c: Crop) => {
    setCrop(c);
  };

  const handleCompletedCrop = (c: Crop) => {
    setCompletedCrop(c);
  };

  const handleConfirm = async () => {
    const blob = await imgPreview(imgRef.current, crop as PixelCrop);

    if (!blob) return;

    const cropUrl = URL.createObjectURL(blob);

    if (blob) {
      const ratio = crop.aspect || 1;

      setImages([
        ...images,
        { blob: blob, url: cropUrl, type: RATIOS[ratio as keyof typeof RATIOS] as ArticleAssetTypeEnum }
      ]);
    }
  };

  const validateCrop = useMemo(() => {
    if (!completedCrop?.aspect || !imgRef.current || !canvasRef.current) return { valid: false };

    canvasPreview(imgRef.current, canvasRef.current, crop);

    /* * Marketing Image minimum size 600x314 * */
    /* * Marketing Image maximum size defined manually by testing * */
    if (completedCrop.aspect === 1.91) {
      if (canvasRef.current.width < 600 || canvasRef.current.height < 314) {
        return { valid: false, message: 'Invalid Image: Marketing Image minimum size 600x314' };
      }
      if (canvasRef.current.width > 2100 || canvasRef.current.height > 1100) {
        return { valid: false, message: 'Invalid Image: Marketing Image maximum size 2100x1100' };
      }
    }

    /* * Square Marketing Image minimum size 300x300 * */
    /* * Square Marketing Image maximum size defined manually by testing * */
    if (completedCrop.aspect === 1) {
      if (canvasRef.current.width < 300 || canvasRef.current.height < 300) {
        return { valid: false, message: 'Invalid Image: Square Marketing Image minimum size 300x300' };
      }
      if (canvasRef.current.width > 2000 || canvasRef.current.height > 2000) {
        return { valid: false, message: 'Invalid Image: Square Marketing Image maximum size 2000X2000' };
      }
    }

    /* * Portrait Marketing Image minimum size 480x600 * */
    /* * Portrait Marketing Image maximum size defined manually by testing * */
    if (completedCrop.aspect === 4 / 5) {
      if (canvasRef.current.width < 480 || canvasRef.current.height < 600) {
        return { valid: false, message: 'Invalid Image: Portrait Marketing Image minimum size 480x600' };
      }
      if (canvasRef.current.width > 1600 || canvasRef.current.height > 2000) {
        return { valid: false, message: 'Invalid Image: Portrait Marketing Image maximum size 1600X2000' };
      }
    }

    return { valid: true };
  }, [completedCrop]);

  useDebounceEffect(
    async () => {
      if (completedCrop?.width && completedCrop?.height && imgRef.current && canvasRef.current) {
        await canvasPreview(imgRef.current, canvasRef.current, completedCrop);
      }
    },
    100,
    [completedCrop]
  );

  useEffect(() => {
    if (!url) return;

    (async () => {
      try {
        const imageURL = `${getKMSBaseUrl()}/graphql/image_proxy?url=${url}`;

        const headers = new Headers({ 'X-Access-Token': Authentication.getToken()! });
        const request = new Request(imageURL, { headers: headers });

        const response = await fetch(request);
        const blob = await response.blob();

        setImgUrl(URL.createObjectURL(blob));
      } catch (e) {
      } finally {
        setLoading(false);
      }
    })();
  }, [url]);

  return {
    loading,
    imgRef,
    crop,
    imgUrl,
    validateCrop,
    isDisplayCampaign,
    handleCrop,
    canvasRef,
    handleCompletedCrop,
    handleConfirm
  };
};
